import React, { useCallback, useState, Children } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDownIcon } from 'lucide-react';
// --- Data ---
const faqs = [{
  question: '¿Cómo recibo el Megapack después de comprar?',
  answer: 'Inmediatamente después de confirmar el pago, recibís un email con el acceso a todos los archivos PDF. En menos de 5 minutos tenés todo disponible para descargar y empezar a usar. Si no te llega el email, escribinos a soporte.edulabs@gmail.com y lo resolvemos de inmediato.'
}, {
  question: '¿Sirve para entrenadores independientes o solo para academias grandes?',
  answer: 'Sirve para ambos. El Megapack fue diseñado específicamente para entrenadores de clubs barriales, escuelas formativas, academias independientes y profes de Educación Física que trabajan con niños y adolescentes. El sistema funciona con 10 alumnos o con 200.'
}, {
  question: '¿Es editable? ¿Puedo modificarlo?',
  answer: 'Los PDF no son editables en su versión base. Están diseñados para usar directamente. Si necesitás una versión editable para personalizar con el logo de tu academia, escribinos a soporte.edulabs@gmail.com y lo coordinamos.'
}, {
  question: '¿Los 6 bonos están en las dos opciones?',
  answer: 'Sí. Los 6 bonos están incluidos en ambas opciones sin costo adicional. La diferencia entre las dos opciones es que la opción completa incluye también Academia Élite 360, el sistema de gestión y escala de la academia.'
}, {
  question: '¿Sirve si soy entrenador nuevo con poca experiencia?',
  answer: 'Especialmente para vos. El Megapack te da exactamente lo que necesitás para arrancar con un sistema profesional desde el primer día. No necesitás experiencia previa. Solo seguir el sistema.'
}, {
  question: '¿Sirve para todas las edades?',
  answer: 'Sí. El sistema cubre desde U4 (niños de 4 años) hasta U16+ con progresión pedagógica específica para cada etapa del desarrollo. Cada tomo está diseñado para una franja de edad específica.'
}, {
  question: '¿Qué pasa si tengo grupos con jugadores de distintos niveles?',
  answer: 'Cada ejercicio tiene 3 variantes de dificultad (fácil, estándar y avanzado). Podés aplicar la variante correcta para cada jugador dentro del mismo ejercicio grupal. El sistema está diseñado para la realidad del entrenador de base.'
}, {
  question: '¿Qué diferencia tiene con otros materiales de fútbol formativo?',
  answer: 'Es el único sistema en español con progresión pedagógica documentada desde U4 hasta U16+, evaluación individual por edad, gestión de padres con plantillas listas, banco de 200 ejercicios indexados, jugadas a balón parado y sistema de gestión de academia incluido. No es un libro de ejercicios sueltos. Es un sistema completo.'
}, {
  question: '¿Por qué elegir el combo con Academia Élite 360?',
  answer: 'Si solo querés mejorar la calidad de tus entrenamientos: el Megapack solo es suficiente. Si querés construir una academia sostenible, llenar cupos, retener alumnos y eventualmente escalar: necesitás Academia Élite 360. Es el sistema que convierte al entrenador en director.'
}, {
  question: '¿Tiene garantía real?',
  answer: 'Sí. 7 días con devolución del 100%. Sin preguntas. Sin formularios. Si no es lo que esperabas, escribís a soporte.edulabs@gmail.com y procesamos el reembolso completo. La tasa de devolución del Megapack es menor al 1%.'
}];
// --- Animation variants ---
const staggerContainer = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.05
    }
  }
};
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 10
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.3
    }
  }
};
const expandCollapse = {
  initial: {
    height: 0,
    opacity: 0
  },
  animate: {
    height: 'auto',
    opacity: 1
  },
  exit: {
    height: 0,
    opacity: 0
  },
  transition: {
    duration: 0.25,
    ease: 'easeInOut'
  }
};
// --- FAQ Item ---
function FAQItem({
  faq,
  index,
  isOpen,
  onToggle
}: {
  faq: {
    question: string;
    answer: string;
  };
  index: number;
  isOpen: boolean;
  onToggle: () => void;
}) {
  return <motion.div variants={fadeUp} className={`rounded-xl overflow-hidden transition-all duration-300 border-l-[3px] ${isOpen ? 'border-[#22C55E]/40 border-l-[#22C55E] bg-white/[0.03]' : 'border-white/[0.05] border-l-transparent bg-white/[0.01] hover:bg-white/[0.02] hover:border-white/10'}`} data-id="element-65">
      <button className="w-full text-left px-5 py-4 sm:py-5 flex items-center justify-between gap-4 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#22C55E]/50 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0a0a0a] rounded-xl" onClick={onToggle} aria-expanded={isOpen} data-id="element-66">
        <span className={`font-bold text-sm sm:text-base transition-colors duration-300 ${isOpen ? 'text-[#22C55E]' : 'text-white'}`} data-id="element-67">
          {index + 1}. {faq.question}
        </span>
        <ChevronDownIcon className={`w-5 h-5 flex-shrink-0 transition-transform duration-300 ${isOpen ? 'text-[#22C55E] rotate-180' : 'text-gray-500'}`} data-id="element-68" />
      </button>
      <AnimatePresence data-id="element-69">
        {isOpen && <motion.div initial={expandCollapse.initial} animate={expandCollapse.animate} exit={expandCollapse.exit} transition={expandCollapse.transition} data-id="element-70">
            <div className="px-5 pb-5 sm:pb-6" data-id="element-71">
              <div className="h-px bg-gradient-to-r from-[#22C55E]/20 to-transparent mb-4" data-id="element-72" />
              <p className="text-gray-300 text-sm leading-relaxed" data-id="element-73">
                {faq.answer}
              </p>
            </div>
          </motion.div>}
      </AnimatePresence>
    </motion.div>;
}
// --- Section ---
export function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const toggle = useCallback((i: number) => {
    setOpenIndex(prev => prev === i ? null : i);
  }, []);
  return <section className="w-full bg-[#0a0a0a] py-16 sm:py-24 px-4" data-id="element-74">
      <div className="max-w-2xl mx-auto" data-id="element-75">
        <motion.div className="text-center mb-10 sm:mb-12" initial={{
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-40px'
      }} transition={{
        duration: 0.5
      }} data-id="element-76">
          <p className="text-[#22C55E]/60 text-xs font-bold tracking-[0.25em] uppercase mb-3" data-id="element-77">
            DUDAS RESUELTAS
          </p>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-white mb-4 leading-tight" data-id="element-78">
            Preguntas <span className="text-[#22C55E]" data-id="element-79">Frecuentes</span>
          </h2>
        </motion.div>

        <motion.div className="space-y-3" initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-40px'
      }} variants={staggerContainer} data-id="element-80">
          {faqs.map((faq, i) => <FAQItem key={i} faq={faq} index={i} isOpen={openIndex === i} onToggle={() => toggle(i)} data-id="element-81" />)}
        </motion.div>
      </div>
    </section>;
}